<!-- @format -->

<template>
	<div class="pc-table">
		<pc-config-provider>
			<div
				:class="[
					'pc-table-btns',
					$slots['button-front'] || $slots['button-backend'] || showActionBtn ? 'pc-table-btns-show' : '',
				]"
			>
				<el-button
					v-for="(item, idx) in actionBtnConfig"
					:key="item.value"
					:loading="actionBtnLoading[item.value]"
					v-bind="getActionBtnProps(item, idx)"
					@click="onActionBtnsAction(item)"
				>
					{{ item.label }}
				</el-button>
				<slot name="button-front"></slot>
				<slot name="button-backend"></slot>
				<el-button v-if="canImport" plain @click="onOpenImport">导入</el-button>
				<el-dropdown v-if="canExport" trigger="click" @command="onOpenExport">
					<el-button plain style="margin-left: 8px">导出</el-button>
					<template #dropdown>
						<el-dropdown-menu>
							<el-dropdown-item v-if="exportProps?.searchExport" command="searchExport">
								搜索结果导出
							</el-dropdown-item>
							<el-dropdown-item v-if="exportProps?.sheetExport" command="sheetExport"> 当前页面导出 </el-dropdown-item>
							<el-dropdown-item v-if="exportProps?.allExport" command="allExport"> 全量导出 </el-dropdown-item>
							<el-dropdown-item v-if="exportProps?.selectExport" command="selectExport">
								选择数据导出
							</el-dropdown-item>
						</el-dropdown-menu>
					</template>
				</el-dropdown>
			</div>
			<div>
				<slot name="title-front"></slot>
			</div>
			<el-table
				ref="tableRef"
				v-loading="loading"
				:data="data"
				:class="['pc-table-content']"
				style="flex: 1"
				v-bind="$attrs"
			>
				<el-table-column
					v-if="multiple"
					type="selection"
					width="50"
					align="left"
					fixed
					reserve-selection
					:selectable="selectable"
				/>
				<el-table-column v-if="index" type="index" width="55" align="left" label="序号" fixed />

				<el-table-column
					v-for="(item, idx) in defaultColumnsList"
					:key="idx"
					:prop="item.code"
					:label="item.label"
					min-width="150"
					v-bind="item.colProps"
				>
					<template #header="scope">
						<slot :name="`column-header-${item.code}`" v-bind="scope" :type="item.type">
							{{ scope.column.label }}
						</slot>
					</template>

					<template #default="scope">
						<slot
							:name="`column-default-${item.code}`"
							v-bind="{ ...scope, ...item.pcProps }"
							:model-value="scope.row[item.code]"
							:disabled="true"
							:type="item.type"
						>
							<component
								:is="getComponent(item.type)"
								:model-value="scope.row[item.code]"
								:disabled="true"
								v-bind="item.pcProps"
							/>
						</slot>
					</template>
				</el-table-column>

				<el-table-column
					v-if="settingColumnConfig?.length"
					prop="pc-table-column-setting"
					label="操作"
					width="150"
					fixed="right"
					v-bind="settingColumnProps"
					class="setting-column-config"
				>
					<template #header="scope">
						<slot name="column-header-column-setting" v-bind="scope">
							{{ scope.column.label }}
						</slot>
						<column-setting
							v-if="canAddMoreColumn"
							ref="checkColumnRef"
							:default-column="checkColumnList"
							:list="columnList"
							:auto-save-column-prop="autoSaveColumnProp"
							@change-column-prop="$emit('changeColumnProp')"
						>
							<el-button icon="Setting" link></el-button>
						</column-setting>
					</template>

					<template #default="scope">
						<slot name="column-default-column-setting" v-bind="scope">
							<setting-btns
								:data="scope"
								:setting-column-config="settingColumnConfig"
								:setting-column-show-length="settingColumnShowLength"
								@setting-action="(type, callback) => settingAction(type, scope, callback)"
							/>
						</slot>
					</template>
				</el-table-column>

				<el-table-column
					v-if="canAddMoreColumn && !settingColumnConfig?.length"
					prop="_add-more-column"
					width="50"
					fixed="right"
				>
					<template #header>
						<column-setting
							ref="checkColumnRef"
							:default-column="checkColumnList"
							:list="columnList"
							:auto-save-column-prop="autoSaveColumnProp"
						>
							<el-button icon="Setting" link></el-button>
						</column-setting>
					</template>
				</el-table-column>
			</el-table>
			<el-pagination
				v-if="showTotal"
				v-model:current-page="pageValue.pageNum"
				v-model:page-size="pageValue.pageSize"
				:total="total"
				:page-sizes="[10, 20, 50, 100]"
				:default-page-size="1"
				background
				class="pc-table-pagination"
				layout="total, sizes, prev, pager, next"
				v-bind="paginationProps"
			/>

			<pc-import v-model="importVisible" title="导入" v-bind="importProps" @on-import="onImport">
				<template #top>
					<slot name="import-top"></slot>
				</template>
				<template #buttom>
					<slot name="import-bottom"></slot>
				</template>
			</pc-import>

			<pc-dialog
				v-model="exportStatus.visabled"
				title="选择字段"
				width="800px"
				confirm-button-text="导出"
				@on-confirm="onExport"
				@on-cancel="onCancel"
			>
				<el-checkbox
					:model-value="exportStatus.checkedAll"
					label="全选"
					size="large"
					:indeterminate="exportStatus.checkedAll === -1"
					@change="handleCheckAllChange"
				/>

				<pc-check-box-list
					ref="exportColumnRef"
					v-model="exportStatus.checkColumnList"
					:list="exportColumnList"
					@change="handleCheckedChange"
				>
					<template #default="scope">
						<slot name="check-column-item" v-bind="scope"> </slot>
					</template>
				</pc-check-box-list>
			</pc-dialog>
		</pc-config-provider>
	</div>
</template>

<script setup lang="ts">
	import { ElTable } from 'element-plus';
	import { download } from '@power/runtime-vue3';
	import SettingBtns from './setting-btns.vue';
	import ColumnSetting from './column-setting.vue';
	import type { ComponentType, SettingColumnConfigType, ExportProps, ActionBtnsConfigType } from './types';
	import { type CheckGroupList } from '@/components/pc-check-box-list/types';
	import PcCheckBoxList from '@/components/pc-check-box-list/index.vue';
	import PcImport from '@/components/pc-import/index.vue';
	import { PcConfigProvider, getComponent, PcDialog, PcModal } from '@/components';
	import { getBtnProps } from '@/utils/btn';

	interface Props {
		data: any[];
		columnList: ComponentType[];
		defaultColumnsList?: ComponentType[];
		defaultCheckColumnsList?: string[];
		loading?: boolean; // 是否加载
		total?: number; // 总数
		showTotal?: boolean;
		canImport?: boolean;
		canExport?: boolean;
		canAddMoreColumn?: boolean;
		multiple?: boolean;
		selectable?: (row: any, index: number) => boolean;
		index?: boolean;
		importProps?: Record<string, any>;
		exportProps?: {
			searchExport?: ExportProps;
			sheetExport?: ExportProps;
			allExport?: ExportProps;
			selectExport?: ExportProps;
		};
		paginationProps?: Record<string, any>;
		settingColumnProps?: Record<string, any>;
		settingColumnShowLength?: number;
		settingColumnConfig?: SettingColumnConfigType[];
		pageValue?: {
			pageSize: number;
			pageNum: number;
		};
		actionBtnConfig?: ActionBtnsConfigType[];
		exportColumnList?: CheckGroupList;
		autoSaveColumnProp?: {
			canAutoSave: boolean;
			module: string;
			page: string;
		};
	}

	const props = withDefaults(defineProps<Props>(), {
		loading: false,
		total: 0,
		settingColumnShowLength: 2,
	});

	const emits = defineEmits([
		'pageChange',
		'onImport',
		'settingAction',
		'actionBtnsAction',
		'update:pageValue',
		'changeColumnProp',
	]);

	const tableRef = ref<InstanceType<typeof ElTable> | null>(null);
	const checkColumnRef = ref<InstanceType<typeof ColumnSetting>>();

	const loading = computed(() => props.loading || checkColumnRef.value?.loading);

	const checkColumnList = computed<string[]>(
		() => props.defaultCheckColumnsList || props.columnList.map((i) => i.code),
	);

	const checkMoreColumnList = computed(() =>
		props.canAddMoreColumn ? checkColumnRef.value?.checkItemList || [] : props.columnList,
	);

	const defaultColumnsList = computed(() =>
		props.defaultColumnsList ? props.defaultColumnsList.concat(checkMoreColumnList.value) : checkMoreColumnList.value,
	);

	const exportColumnList = computed(() => props.exportColumnList || props.columnList);

	const showActionBtn = computed(() => {
		return props.actionBtnConfig?.length || props.canExport || props.canImport;
	});

	const pageValue = computed({
		get() {
			return props.pageValue || { pageNum: 1, pageSize: 20 };
		},
		set(val) {
			emits('update:pageValue', val);
		},
	});

	const exportStatus = ref<any>({
		visabled: false,
		checkColumnList: [],
		exportType: '',
		checkedAll: true,
	});

	const importVisible = ref(false);

	const actionBtnLoading = ref<Record<string, boolean>>({});

	const getActionBtnProps = (item, idx) => {
		const params = getBtnProps(item.value);
		if (idx === 0) {
			return {
				...params,
				type: 'primary',
				...item.pcProps,
			};
		} else {
			return {
				plain: true,
				...item.pcProps,
			};
		}
	};

	const onActionBtnsAction = (item) => {
		actionBtnLoading.value[item.value] = true;
		const callback = (cb?: Function) => {
			emits(
				'actionBtnsAction',
				item.value,
				() => {
					cb && cb();
					actionBtnLoading.value[item.value] = false;
				},
				item,
			);
		};

		if (item?.twiceConfirm) {
			const { ...conProp } = (item.confirmProps || {}) as any;

			PcModal.PcMessageBox({ ...conProp, message: conProp.message || conProp.content })
				.then(({ done }) => {
					callback(done);
				})
				.catch(({ done }) => {
					done();
					actionBtnLoading.value[item.value] = false;
				});
		} else {
			callback();
		}
	};

	const settingAction = (type, scope, callback) => {
		emits('settingAction', type, scope, callback);
	};

	const onOpenImport = () => {
		importVisible.value = true;
	};

	const onImport = (params, callback: Function) => {
		importVisible.value = false;
		emits('onImport', params, callback);
	};

	const onOpenExport = (type) => {
		if (exportColumnList.value.length) {
			exportStatus.value = {
				visabled: true,
				checkColumnList: (exportColumnList.value || []).map((i) => i.code),
				exportType: type,
				checkedAll: true,
			};
		} else {
			exportStatus.value = {
				visabled: false,
				checkColumnList: [],
				exportType: type,
				checkedAll: true,
			};
			onExport();
		}
	};

	const handleCheckAllChange = (val) => {
		exportStatus.value.checkColumnList = val ? (exportColumnList.value || []).map((i) => i.code) : [];
		exportStatus.value.checkedAll = val;
	};

	const handleCheckedChange = (value: string[]) => {
		const checkedCount = value.length;
		if (checkedCount === props.columnList.length) {
			exportStatus.value.checkedAll = true;
		} else if (!checkedCount) {
			exportStatus.value.checkedAll = false;
		} else {
			exportStatus.value.checkedAll = -1;
		}
	};

	const onExport = async (callback = () => {}) => {
		const item = (props.exportProps || {})[exportStatus.value.exportType];
		if (item) {
			if (typeof item === 'function') {
				await item(exportStatus.value.checkColumnList);
			} else if (typeof item.params === 'function') {
				const par = item.params(exportStatus.value.checkColumnList);
				download(item.url, par, item.name, item.headers);
			} else {
				download(item.url, item.params, item.name, item.headers);
			}
		}
		exportStatus.value.visabled = false;
		callback();
	};

	const onCancel = (callback: Function) => {
		exportStatus.value.visabled = false;
		callback();
	};

	watch(
		pageValue,
		(newVal) => {
			emits('pageChange', newVal);
			tableRef.value?.scrollTo({
				left: 0,
				top: 0,
				behavior: 'smooth',
			});
		},
		{
			deep: true,
		},
	);

	watch(
		() => pageValue.value.pageSize,
		(newVal) => {
			pageValue.value.pageNum = 1;
			console.log(newVal, '1111111111111111');
		},
	);

	defineExpose({ tableRef, onOpenImport, onOpenExport, checkColumnRef });
</script>

<style scoped lang="scss">
	.pc-table {
		width: 100%;
		height: 100%;
		display: flex;
		flex-direction: column;

		.pc-table-btns {
			display: flex;
		}

		.pc-table-btns-show {
			margin-bottom: 16px;
		}

		.pc-table-pagination {
			justify-content: flex-end;
			margin-top: 5px;
		}

		:deep(.cell) {
			.el-button.is-link {
				width: 100%;
				justify-content: flex-start;

				> span {
					white-space: nowrap;
					text-overflow: ellipsis;
					overflow: hidden;
					display: inline-block;
				}
			}
		}
	}
</style>
